import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useKvkkConsentsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const Container = styled.div``;

export default function KvkkConsentsPage() {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [revokeConfirm, setRevokeConfirm] = useState({ isOpen: false, id: null, scope: '' });
  const [revoking, setRevoking] = useState(false);

  const { fetching, data, pagination, columns: baseColumns, revoke } = useKvkkConsentsApi(queryParams);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRevokeClick = useCallback((row) => {
    setRevokeConfirm({ isOpen: true, id: row.id, scope: row.scope });
  }, []);

  const handleRevokeConfirm = useCallback(async () => {
    if (!revokeConfirm.id) return;
    setRevoking(true);
    await revoke(revokeConfirm.id, 'admin_action');
    setRevokeConfirm({ isOpen: false, id: null, scope: '' });
    setRevoking(false);
  }, [revokeConfirm.id, revoke]);

  // Only show Revoke for currently-active rows. Revoked / superseded rows
  // are read-only — re-revoking them is meaningless and would broadcast a
  // duplicate kvkk_consent_revoked event.
  const buildActions = useCallback((row) => {
    const isActive = row && !row.revokedAt;
    if (!isActive) return [];
    return [
      { type: 'delete', label: t('kvkkConsent.revoke'), onClick: handleRevokeClick },
    ];
  }, [t, handleRevokeClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const actions = buildActions(row);
          if (actions.length === 0) return null;
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={revokeConfirm.isOpen}
        title={t('kvkkConsent.revokeTitle')}
        message={t('kvkkConsent.revokeMessage', { scope: revokeConfirm.scope })}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevokeConfirm({ isOpen: false, id: null, scope: '' })}
        loading={revoking}
      />

      {useMemo(() => (
        <DataTable
          title={t('kvkkConsent.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
